import { Modal } from "antd";
import { ReactNode, useMemo } from "react";
import { ImpactSummaryDataType } from "../model";
import {
  ADDED_ACTIVITIES_CARD_MESSAGE,
  formatDeletedLinkedMessage,
  formatUpdatedDeadlinesMessage,
  getMakeActiveImpactCounts,
  hasReviewChangeImpact
} from "./make-active-warning-modal-utils";

type MakeActiveWarningModalProps = {
  scheduleImpactSummary: ImpactSummaryDataType;
  fullSelectedScheduleName: string | undefined;
  isMakeScheduleActiveModalOpen: boolean;
  setMakeScheduleActiveModalOpen: (open: boolean) => void;
  handleProceedTosetActive: () => void | Promise<void>;
  isMakingActiveLoading: boolean;
};

type ImpactActivityCardProps = {
  title: string;
  activityCount: number;
  variant?: "deleted" | "default";
  children?: ReactNode;
};

function ImpactActivityCard({
  title,
  activityCount,
  variant = "default",
  children
}: ImpactActivityCardProps) {
  const isDeletedWithActivities = variant === "deleted" && activityCount > 0;

  return (
    <div
      className="rounded border border-solid p-2 border-ciq-gray-300 bg-white"
      data-testid={`make-active-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="mb-1 flex items-center gap-2">
        <span className="text-black text-opacity-90 text-sm">{title}</span>
        <span
          className={`rounded-full border border-solid border-ciq-gray-300 px-2 py-0.5 text-xs ${isDeletedWithActivities ? "text-ciq-red-700" : "text-black text-opacity-90"}`}
        >
          {activityCount}
        </span>
      </div>
      {children ? (
        <div className="text-xs text-black text-opacity-65">{children}</div>
      ) : null}
    </div>
  );
}

function MakeActiveWarningModal({
  scheduleImpactSummary,
  fullSelectedScheduleName,
  isMakeScheduleActiveModalOpen,
  setMakeScheduleActiveModalOpen,
  handleProceedTosetActive,
  isMakingActiveLoading
}: MakeActiveWarningModalProps) {
  const counts = useMemo(
    () => getMakeActiveImpactCounts(scheduleImpactSummary),
    [scheduleImpactSummary]
  );

  const showReviewChangeImpact = hasReviewChangeImpact(counts);

  const deletedCardMessage = formatDeletedLinkedMessage(
    counts.deletedMaterials,
    counts.deletedSubmittals
  );
  const updatedCardMessage = formatUpdatedDeadlinesMessage(
    counts.updatedMaterials,
    counts.updatedSubmittals
  );

  const modalTitle = showReviewChangeImpact
    ? "Make Version Active?"
    : "Change Schedule?";

  return (
    <Modal
      title={modalTitle}
      open={isMakeScheduleActiveModalOpen}
      onOk={handleProceedTosetActive}
      okText="Confirm"
      closable={!isMakingActiveLoading}
      onCancel={() => {
        setMakeScheduleActiveModalOpen(false);
      }}
      okButtonProps={{
        disabled: isMakingActiveLoading,
        loading: isMakingActiveLoading
      }}
      cancelButtonProps={{ disabled: isMakingActiveLoading }}
      width={showReviewChangeImpact ? 520 : 380}
      destroyOnHidden
      mask={{ closable: !isMakingActiveLoading }}
      data-testid="index-modal-schedule"
    >
      <div data-testid="schedule-active-version-message">
        {showReviewChangeImpact ? (
          <div className="flex flex-col gap-3">
            <p className="mb-0 text-sm text-black text-opacity-80">
              Impact of{" "}
              <span className="font-semibold">{fullSelectedScheduleName}</span>{" "}
              on Procurement Plan:
            </p>

            <div className="flex flex-col space-y-4">
              <ImpactActivityCard
                title="Deleted Activities"
                activityCount={counts.deletedActivities}
                variant="deleted"
              >
                {deletedCardMessage}
              </ImpactActivityCard>

              <ImpactActivityCard
                title="Updated Activities"
                activityCount={counts.updatedActivities}
              >
                {updatedCardMessage}
              </ImpactActivityCard>

              <ImpactActivityCard
                title="Added Activities"
                activityCount={counts.addedActivities}
              >
                {ADDED_ACTIVITIES_CARD_MESSAGE}
              </ImpactActivityCard>
            </div>

            <p className="mb-0 text-sm text-black text-opacity-90">
              Do you want to continue to make this version active?
            </p>
          </div>
        ) : (
          <p className="mb-0 text-sm text-black text-opacity-90 leading-7 ">
            <span className="font-semibold">
              {fullSelectedScheduleName ?? "This schedule"}
            </span>{" "}
            has no changes in activities from the current schedule.
            <br />
            Do you still want to make this version active?
          </p>
        )}
      </div>
    </Modal>
  );
}

export default MakeActiveWarningModal;
